// src/components/Codebook/PublicCodebookViewer.tsx
// Read-only viewer for publicly shared codebooks — no Run buttons, no PDF, no Share

import React from 'react';
import { CodebookGetResponse, isNarrativeBlock, isCodeBlock, isCodeDualBlock } from '../../types/chat';
import CodebookNarrativeBlock from './CodebookNarrativeBlock';
import CodebookCodeBlock from './CodebookCodeBlock';
import CodebookDualCodeBlock from './CodebookDualCodeBlock';

interface PublicCodebookViewerProps {
  codebook: CodebookGetResponse;
  onBack: () => void;
}

const PublicCodebookViewer: React.FC<PublicCodebookViewerProps> = ({ codebook, onBack }) => {
  return (
    <div className="codebook-viewer h-full flex flex-col">
      {/* Header with back navigation only */}
      <div className="flex-shrink-0 border-b border-border bg-background/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-card rounded-lg transition-colors text-muted-foreground hover:text-foreground"
              title="Back"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div>
              <h2 className="text-lg font-semibold">{codebook.title}</h2>
              <p className="text-xs text-muted-foreground italic">{codebook.prompt}</p>
              <p className="text-xs text-muted-foreground">
                {codebook.total_blocks} blocks • {codebook.code_blocks} code examples
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content area with scrolling */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
          {/* Metadata */}
          <div className="text-sm text-muted-foreground space-y-1">
            <p>Generated: {new Date(codebook.created_at).toLocaleDateString()}</p>
            <p>Reading time: {Math.max(1, Math.ceil(codebook.word_count / 200))} min</p>
          </div>

          {/* Content blocks — all code blocks rendered in readOnly mode */}
          {codebook.blocks.length > 0 ? (
            <div className="space-y-8">
              {codebook.blocks.map((block, idx) => (
                <div key={block.artifact_id || idx}>
                  {isNarrativeBlock(block) ? (
                    <CodebookNarrativeBlock content={block.content.markdown} />
                  ) : isCodeDualBlock(block) ? (
                    <CodebookDualCodeBlock
                      language_primary={block.content.primary.language}
                      code_primary={block.content.primary.code}
                      language_secondary={block.content.secondary.language}
                      code_secondary={block.content.secondary.code}
                      comparison_notes={block.content.comparison_notes}
                      codebookId={codebook.codebook_id}
                      blockIndex={idx}
                      readOnly={true}
                    />
                  ) : isCodeBlock(block) ? (
                    <CodebookCodeBlock
                      language={block.content.language}
                      code={block.content.code}
                      explanation={block.content.explanation}
                      codebookId={codebook.codebook_id}
                      blockIndex={idx}
                      readOnly={true}
                      setupSql={block.content.setup_sql}
                    />
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No content available</p>
            </div>
          )}

          {/* Footer spacing */}
          <div className="h-12" />
        </div>
      </div>
    </div>
  );
};

export default PublicCodebookViewer;
